import { Component, OnDestroy, effect, inject } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';

import { NetworkStatusService } from '@core/services/network-status.service';
import { CatchesService } from '@core/services/catches.service';
import { CatchDownloadService } from '@core/services/catch-download.service';
import { AppUpdateService } from '@core/services/app-update.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [IonApp, IonRouterOutlet],
  template: `
    <ion-app>
      <ion-router-outlet></ion-router-outlet>
    </ion-app>
  `,
})
export class AppComponent implements OnDestroy {
  private readonly networkStatus = inject(NetworkStatusService);
  private readonly catchesService = inject(CatchesService);
  private readonly catchDownload = inject(CatchDownloadService);
  private readonly appUpdate = inject(AppUpdateService);

  /**
   * Track connectivity so we can automatically retry POSTing any
   * offline-created catches the moment the user regains a connection.
   * Uses effect() to subscribe to the isOnline signal: fires once on
   * startup with the current value, then again every transition.
   *
   * We only care about the false-to-true edge — becoming online is
   * the trigger for a sync attempt. On the first run at startup when
   * the user is already online, this also sweeps up any catches that
   * were created in a prior session but never made it to the server.
   */
  private readonly wasOnline = { value: false };

  /**
   * Bound handler for visibilitychange so removeEventListener
   * actually finds the same reference on cleanup.
   */
  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible' && this.networkStatus.isOnline()) {
      // The user just brought the PWA back to the foreground (returned
      // from another tab, returned from another app on mobile, etc.).
      // Check if there are new catches on the server. The service
      // applies a cooldown so rapid switches don't generate request
      // storms — see CatchDownloadService.quickSyncIfStale.
      this.catchDownload.quickSyncIfStale();
    }
  };

  constructor() {
    effect(() => {
      const online = this.networkStatus.isOnline();
      if (online && !this.wasOnline.value) {
        // Fire-and-forget. Any failure is logged; there's nowhere to
        // surface it meaningfully at the app-shell level.
        this.catchesService
          .syncPending()
          .then((n) => {
            if (n > 0) console.log(`Synced ${n} pending catch(es) after reconnect.`);
          })
          .catch((err) => console.warn('Reconnect sync failed', err));

        // Also check for new catches added on other devices since
        // we last looked. quickSyncIfStale is a no-op if the user
        // isn't logged in or if the cooldown hasn't elapsed, so
        // calling it on every reconnect is safe.
        this.catchDownload.quickSyncIfStale();
      }
      this.wasOnline.value = online;
    });

    // App startup: if the user is already logged in (e.g. they're
    // returning to the PWA after closing the tab), check for new
    // catches. Wrapped in a small delay so it doesn't compete with
    // the initial bootstrap (route resolution, reference data loads,
    // etc.). The cooldown protects us from running this AND the
    // login.page.ts post-login download in quick succession.
    setTimeout(() => this.catchDownload.quickSyncIfStale(), 1500);

    // Visibility change: PWA brought back into focus. Same check.
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // Start watching for app updates so users see a "Reload" prompt
    // when new code is deployed, instead of having to manually quit
    // and reopen the installed PWA.
    this.appUpdate.init();
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }
}
